import { type Locale, SUPPORTED_LOCALES } from '@developerz/i18n';
import type { JSX } from 'solid-js';
import { LOCALE_ENDONYMS, readStoredLocale, t, writeStoredLocale } from '../i18n';
import { Select, type SelectOption } from './Select';

// The top bar's language picker (#6222), beside the theme toggle: every
// supported locale by its own name, plus "Browser default", which clears the
// stored choice. The control shows the STORED choice, not the resolved locale —
// a zh browser with nothing stored reads "Browser default", never "中文", or
// the way back to following the browser could not show as current.
//
// Choosing saves and RELOADS: `t` is built once at load (`i18n.ts`), and so is
// every formatter in `lib/time.ts`, so the only honest language change is a
// fresh page. `reload` is injectable so a test can see it fire. A pick of the
// current choice is a no-op (`Select` never calls `onChange` for it), so it
// never reloads for nothing.
//
// The themed `Select`, not a native one (`styles/native-select-ratchet.test.ts`
// bans those), named by a visually hidden `label[for]` with the `${id}-label`
// id its listbox's `aria-labelledby` needs — `SeatCheckout`'s shape.
//
// MOUNTED TWICE, which is why `id` and `class` are props: in the top bar
// (`AppTopbar`, `.app-topbar__locale`, hidden below `sm` — a 390px bar has no
// room for a trigger) and in the nav drawer (`AppSidebar`,
// `.app-sidebar__locale`, hidden from `sm` up), so a phone switches from the
// drawer and every width shows exactly one. A shared id would point both
// labels at the first control.

/** `''` is the listed "Browser default": no stored choice. */
type Choice = Locale | '';

/** The language select: Browser default plus each supported locale by its own name; choosing saves `dz-locale` and reloads. */
export function LanguagePicker(props: {
  /** The control's id, unique per mount; also the label's `for`. */
  readonly id: string;
  /** The placement's wrapper class: it decides at which widths this mount shows. */
  readonly class: string;
  /** A full-width trigger (`Select`'s own `block`), for the drawer's column. */
  readonly block?: boolean;
  /** Reloads the page after a choice; injected by tests, `location.reload` otherwise. */
  readonly reload?: () => void;
}): JSX.Element {
  /** "Browser default" first, then every supported locale by its endonym. */
  const options = (): readonly SelectOption<Choice>[] => [
    { value: '', label: t('locale.browser_default') },
    ...SUPPORTED_LOCALES.map((locale) => ({ value: locale, label: LOCALE_ENDONYMS[locale] })),
  ];

  /** Save `choice` (or clear it for Browser default) and reload into it. */
  const choose = (choice: Choice): void => {
    writeStoredLocale(choice === '' ? null : choice);
    (props.reload ?? (() => location.reload()))();
  };

  return (
    <div class={props.class}>
      <label id={`${props.id}-label`} for={props.id} class="sr-only">
        {t('locale.label')}
      </label>
      <Select
        a11y={{ id: props.id }}
        label={t('locale.label')}
        value={readStoredLocale() ?? ''}
        options={options()}
        block={props.block === true}
        onChange={choose}
      />
    </div>
  );
}
